import { Link } from "react-router-dom";
import {
  CheckCircle2,
  ShieldCheck,
  FileText,
  Clock,
  Users,
  ArrowRight,
  Lock,
  Sparkles,
} from "lucide-react";
import { SiteLayout } from "@/components/site/SiteLayout";
import { HeroBackground } from "@/components/site/HeroBackground";
import { PageSEO } from "@/components/site/PageSEO";
import { SectionHeading } from "@/components/site/SectionHeading";
import { CTASection } from "@/components/site/CTASection";
import { LeadMagnetCTA } from "@/components/site/LeadMagnetCTA";
import { Reveal, RevealStagger, RevealItem } from "@/components/motion/Reveal";
import { brand } from "@/data/site/brand";
import { stockImages } from "@/data/site/stockImages";
import coverAsset from "@/assets/lead-magnet/7-secrets-cover.png.asset.json";

const path = "/resources/7-secrets-guide";

const secrets = [
  {
    n: "01",
    title: "The IRS will settle for less than you owe — but they won't tell you how",
    body: "The Offer in Compromise program exists by statute (IRC §7122), but the IRS has no obligation to suggest it. You have to know it exists, qualify under their Reasonable Collection Potential formula, and submit Form 656 the right way the first time.",
  },
  {
    n: "02",
    title: "Every tax debt has an expiration date",
    body: "The Collection Statute Expiration Date (CSED) wipes out IRS debt — usually 10 years from assessment. Most taxpayers never pull their account transcripts to find theirs. Knowing your CSED changes the entire negotiation.",
  },
  {
    n: "03",
    title: "You can stop a wage garnishment in days, not months",
    body: "A properly filed Collection Due Process request (Form 12153) or a documented financial hardship case can pause levies fast. The window to act is tight — usually 30 days from the LT11/Letter 1058 notice.",
  },
  {
    n: "04",
    title: "Penalties are negotiable — even after you've paid them",
    body: "First-Time Penalty Abatement, reasonable cause, and statutory exceptions can erase failure-to-file and failure-to-pay penalties. We've seen six-figure penalty stacks disappear with one well-supported request.",
  },
  {
    n: "05",
    title: "Currently Not Collectible status is real, and the IRS won't volunteer it",
    body: "If collection would create economic hardship, the IRS must place your account in CNC (status 53). Collections stop, the CSED keeps running, and the debt can expire entirely without you paying it.",
  },
  {
    n: "06",
    title: "An Installment Agreement is not always the best deal",
    body: "Default IAs often pay back more than you owe (penalties + interest keep accruing). A Partial Pay Installment Agreement, Offer in Compromise, or CNC plus CSED expiration may legally cost a fraction of what you think.",
  },
  {
    n: "07",
    title: "Innocent Spouse Relief can erase a spouse's tax debt from your record",
    body: "If your spouse (or ex-spouse) caused the liability and you qualify under IRC §6015, the IRS must remove the debt from your account. Form 8857 + proper documentation is the path — most people never know it exists.",
  },
];

const insideTheGuide = [
  { icon: FileText, label: "23 pages", sub: "Plain-English, no jargon" },
  { icon: Clock, label: "15-min read", sub: "Skim it, save it, share it" },
  { icon: Users, label: "Written by CPAs", sub: `${brand.yearsExperience}+ yrs experience` },
  { icon: ShieldCheck, label: "Free forever", sub: "No upsell, no sales call" },
];

const faqs = [
  {
    q: "Do I have to give my email to download it?",
    a: "Yes — name and email. We use it once to confirm the download and to send you a short follow-up if you want a free case review. We don't sell your information and we don't add you to a marketing list.",
  },
  {
    q: "Is the advice actually useful, or just a sales pitch?",
    a: "It's the same playbook we use internally for new clients. Every secret cites the IRC section, the IRS form, and what to do next. If your case is straightforward, you may be able to act on it without ever hiring us.",
  },
  {
    q: "What if I want help applying any of this to my situation?",
    a: "Book a free consultation. A credentialed CPA — not a salesperson — reads your IRS notices, pulls your transcript history if helpful, and tells you honestly whether resolution makes sense.",
  },
];

export default function SevenSecretsGuide() {
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "Book",
      name: "7 Things the IRS Does Not Want You to Know About",
      author: {
        "@type": "Organization",
        name: brand.legalName,
        url: brand.siteUrl,
      },
      publisher: {
        "@type": "Organization",
        name: brand.legalName,
        url: brand.siteUrl,
      },
      bookFormat: "https://schema.org/EBook",
      inLanguage: "en-US",
      isAccessibleForFree: true,
      image: coverAsset.url,
      url: `${brand.siteUrl}${path}`,
      description:
        "Free 23-page guide from CPAs at Comprehensive Tax Resolution covering IRS settlement options, collection statute expiration, levy and garnishment defense, penalty abatement, and innocent spouse relief.",
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqs.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
  ];

  return (
    <SiteLayout>
      <PageSEO
        title="Free Guide: 7 Things the IRS Doesn't Want You to Know"
        description="Download our free 23-page CPA-written guide. Settlement options, statute expiration, penalty abatement, and the IRS programs they never advertise. No sales call required."
        path={path}
        image={coverAsset.url}
        jsonLd={jsonLd}
        breadcrumbs={[
          { name: "Resources", href: "/resources" },
          { name: "7 Secrets Guide", href: path },
        ]}
      />

      {/* Hero */}
      <section className="relative bg-gradient-navy text-white overflow-hidden">
        <HeroBackground image={stockImages.accountingDesk} priority intensity="strong" />
        <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <div className="grid items-center gap-12 lg:grid-cols-[1fr_auto]">
            <Reveal>
              <div className="max-w-2xl">
                <Link
                  to="/resources"
                  className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-gold/90 hover:text-gold"
                >
                  ← Resources
                </Link>
                <span className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-gold">
                  <Sparkles className="h-3 w-3" /> Free • Instant PDF
                </span>
                <h1 className="mt-4 font-heading text-4xl lg:text-6xl font-bold leading-[1.05] [text-wrap:balance]">
                  7 Things the IRS Does{" "}
                  <span className="text-gold">Not Want You to Know About.</span>
                </h1>
                <p className="mt-5 max-w-xl text-base lg:text-lg text-white/85 leading-relaxed">
                  The same insider playbook our CPAs use with new clients —
                  written down, given to you free. Settlement options, statute
                  expiration, penalty abatement, and the IRS programs they
                  never advertise.
                </p>
                <div className="mt-6 flex flex-wrap gap-x-6 gap-y-3 text-sm text-white/75">
                  <span className="inline-flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-gold" /> 23 pages of CPA strategy
                  </span>
                  <span className="inline-flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-gold" /> Cites every IRS form &amp; code section
                  </span>
                  <span className="inline-flex items-center gap-2">
                    <Lock className="h-4 w-4 text-gold" /> No sales call required
                  </span>
                </div>
                <a
                  href="#download"
                  className="mt-8 inline-flex items-center gap-2 rounded-md bg-gold px-7 py-4 text-sm font-bold uppercase tracking-wider text-navy-dark shadow-lg transition-transform hover:scale-[1.02] hover:bg-gold-light"
                >
                  Download the free guide <ArrowRight className="h-4 w-4" />
                </a>
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="hidden lg:block">
                <div className="relative w-[280px]">
                  <div className="absolute inset-0 -rotate-2 rounded-md bg-gold/30 blur-md" aria-hidden />
                  <div className="absolute -left-2 top-2 h-full w-full -rotate-3 rounded-md bg-white/40" aria-hidden />
                  <img
                    src={coverAsset.url}
                    alt="Cover of the free guide: 7 Things the IRS Does Not Want You to Know About"
                    width={620}
                    height={802}
                    className="relative block w-full h-auto rounded-md shadow-2xl ring-1 ring-black/10"
                  />
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>


      {/* What's inside */}
      <section className="border-y border-border bg-secondary/40 py-10">
        <div className="container mx-auto px-4">
          <RevealStagger className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {insideTheGuide.map((i) => (
              <RevealItem key={i.label}>
                <div className="flex items-start gap-3">
                  <div className="shrink-0 w-11 h-11 rounded-lg bg-white flex items-center justify-center text-navy ring-1 ring-border">
                    <i.icon className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="font-heading text-base font-bold text-navy-dark leading-tight">
                      {i.label}
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5">{i.sub}</p>
                  </div>
                </div>
              </RevealItem>
            ))}
          </RevealStagger>
        </div>
      </section>

      {/* The 7 secrets */}
      <section className="py-16 lg:py-24">
        <div className="container mx-auto px-4 max-w-4xl">
          <SectionHeading
            eyebrow="What you'll learn"
            title="The 7 secrets, in plain English"
            intro="Every chapter inside the guide goes deeper — with the exact IRS form numbers, code citations, deadlines, and step-by-step instructions you can actually use."
          />
          <RevealStagger className="mt-12 grid gap-5">
            {secrets.map((s) => (
              <RevealItem key={s.n}>
                <article className="group flex gap-5 rounded-xl border border-border bg-white p-6 hover:border-navy/40 hover:shadow-md transition-all">
                  <div className="shrink-0 w-14 h-14 rounded-lg bg-secondary flex items-center justify-center text-navy font-heading text-lg font-bold group-hover:bg-gold group-hover:text-navy-dark transition-colors">
                    {s.n}
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-heading text-lg lg:text-xl font-bold text-navy-dark leading-snug">
                      {s.title}
                    </h3>
                    <p className="mt-2 text-sm lg:text-base text-muted-foreground leading-relaxed">
                      {s.body}
                    </p>
                  </div>
                </article>
              </RevealItem>
            ))}
          </RevealStagger>
        </div>
      </section>

      {/* Lead magnet form */}
      <div id="download" className="scroll-mt-24">
        <LeadMagnetCTA />
      </div>

      {/* Who it's for */}
      <section className="py-16 lg:py-20 bg-white">
        <div className="container mx-auto px-4 max-w-5xl">
          <SectionHeading
            eyebrow="Who this guide is for"
            title="If any of these sound like you, download it"
          />
          <div className="mt-10 grid gap-4 md:grid-cols-2">
            {[
              "You owe the IRS $10,000 or more and don't know your real options",
              "You've received a CP14, CP504, LT11, or CP2000 notice",
              "Your wages or bank account have been levied — or you've been warned they will be",
              "You haven't filed returns in one or more years and don't know how to get current",
              "You're being threatened with a federal tax lien on your home or business",
              "You think you might qualify for an Offer in Compromise but don't trust the TV ads",
            ].map((line) => (
              <div
                key={line}
                className="flex items-start gap-3 rounded-lg border border-border bg-secondary/40 p-4"
              >
                <CheckCircle2 className="h-5 w-5 shrink-0 mt-0.5 text-navy" />
                <p className="text-sm lg:text-base text-navy-dark leading-snug">{line}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 lg:py-20 bg-secondary/30">
        <div className="container mx-auto px-4 max-w-3xl">
          <SectionHeading eyebrow="Common questions" title="About this guide" />
          <div className="mt-10 grid gap-6">
            {faqs.map((f) => (
              <div key={f.q} className="rounded-xl bg-white border border-border p-6">
                <h3 className="font-heading text-base lg:text-lg font-bold text-navy-dark">
                  {f.q}
                </h3>
                <p className="mt-2 text-sm lg:text-base text-muted-foreground leading-relaxed">
                  {f.a}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <CTASection />
    </SiteLayout>
  );
}
